import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

interface AllureProject {
  data: {
    project: {
      id: string;
      reports_id: string[];
      reports: string[];
    };
  };
}

export interface AllureInputs {
  env: string;
  website: string;
  business_unit: string;
  country: string;
}

/**
 * Build Allure project name from workflow inputs
 * Pattern: {env}-{website}-{business_unit}-{country}
 * Example: staging-odoo-benandfrank-mx
 */
export function buildProjectName(inputs: AllureInputs): string {
  return `${inputs.env}-${inputs.website}-${inputs.business_unit}-${inputs.country}`;
}

/**
 * Fetch Allure report summary statistics from backend
 * Backend uses cache-first strategy with 7-day TTL
 */
export async function fetchAllureReportSummary(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  project: string,
  reportId: string = 'latest',
): Promise<{
  project: string;
  reportId: string;
  reportUrl: string;
  statistic: {
    passed: number;
    failed: number;
    broken: number;
    skipped: number;
    total: number;
  };
  percentage: number;
  fetchedAt?: string;
  from_cache?: boolean;
} | null> {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('qa-automated-tests');
    const response = await fetchApi.fetch(
      `${baseUrl}/allure/reports/${project}/${reportId}/summary`,
    );

    if (!response.ok) {
      // eslint-disable-next-line no-console
      console.warn(
        `[QA Plugin] Failed to fetch Allure report summary for ${project}:`,
        response.status,
      );
      return null;
    }

    return response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `[QA Plugin] Error fetching Allure report summary for ${project}:`,
      error,
    );
    return null;
  }
}

/**
 * Get Allure project information
 */
export async function allureProject(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  project: string,
): Promise<AllureProject> {
  const baseUrl = await discoveryApi.getBaseUrl('qa-automated-tests');
  const response = await fetchApi.fetch(`${baseUrl}/allure/projects/${project}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch Allure project: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Find the Allure report URL by fetching workflow run logs from GitHub
 * The report URL is embedded in the workflow logs when the "publish-report" step runs
 */
export async function findAllureReportUrl(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  _inputs: AllureInputs,  // Kept for backward compatibility, but not used
  runId: string,  // GitHub workflow run databaseId
): Promise<string | null> {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('qa-automated-tests');
    const response = await fetchApi.fetch(`${baseUrl}/runs/${runId}/logs`);
    
    if (!response.ok) {
      // Log not found or report URL not parsed - return null
      return null;
    }
    
    const data = await response.json();
    return data.reportUrl || null;
  } catch (error) {
    // Return null if there's an error fetching the logs
    return null;
  }
}

/**
 * Get the Allure project URL
 */
export function getAllureProjectUrl(discoveryApi: DiscoveryApi, inputs: AllureInputs): Promise<string> {
  const projectName = buildProjectName(inputs);
  return discoveryApi.getBaseUrl('qa-automated-tests').then(url => `${url}/allure/projects/${projectName}`);
}

/**
 * Check if backend plugin is healthy and endpoints are available
 */
export async function checkBackendHealth(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<{ healthy: boolean; version?: string; error?: string }> {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('qa-automated-tests');
    const response = await fetchApi.fetch(`${baseUrl}/health`);
    
    if (!response.ok) {
      return { healthy: false, error: `Health check failed: ${response.status}` };
    }
    
    const data = await response.json();
    return {
      healthy: data.status === 'healthy',
      version: data.version,
    };
  } catch (error) {
    return {
      healthy: false,
      error: error instanceof Error ? error.message : 'Health check failed',
    };
  }
}

/**
 * Parse project name from Allure report URL
 * URL: http://allure.apps.casimiros.com/allure-docker-service/projects/staging-odoo-benandfrank-mx/reports/142/index.html
 * Returns: staging-odoo-benandfrank-mx
 */
export function parseProjectFromUrl(reportUrl: string): string | null {
  const match = reportUrl.match(/\/projects\/([^/]+)/);
  return match ? match[1] : null;
}

/**
 * Parse report ID from Allure report URL
 * URL: .../projects/staging-odoo-benandfrank-mx/reports/142/index.html
 * Returns: 142
 */
export function parseReportIdFromUrl(reportUrl: string): string | null {
  const match = reportUrl.match(/\/reports\/(\d+)/);
  return match ? match[1] : null;
}

/**
 * Transform external Allure URL to Backstage proxy URL
 * External: http://allure.apps.casimiros.com/allure-docker-service/projects/{project}/reports/{report}/index.html
 * Proxy: /api/proxy/allure/projects/{project}/reports/{report}/index.html
 */
export function transformToProxyUrl(reportUrl: string): string | null {
  const project = parseProjectFromUrl(reportUrl);
  const reportId = parseReportIdFromUrl(reportUrl);
  
  if (!project || !reportId) {
    return null;
  }
  
  return `/api/proxy/allure/projects/${project}/reports/${reportId}/index.html`;
}
